import { ApolloClient } from 'apollo-client'
import { NormalizedCacheObject } from 'apollo-cache-inmemory'
import { useHistory } from 'react-router-dom'
import { History } from 'history'
import { useApolloClient } from '@apollo/react-hooks'
import * as Sentry from '@sentry/browser'

import { routes, authorizationKeys, localStorageKeys } from '@sketch/constants'
import {
  GetInitialUserDocument,
  GetInitialUserQuery,
  useRevokeOAuthMutation,
} from '@sketch/gql-types'
import { initializeLocalApolloState } from '@sketch/graphql-cache'
import { removeItem, useEventDispatch } from '@sketch/utils'
import {
  getActiveAuthorization,
  getAllAuthorizations,
  getUserAuthorization,
  removeActiveAuthorization,
  removeAuthorizationByAuthToken,
} from 'graphql/multisession'
import { useHubspotChat } from 'utils/hooks/useHubspotChat'
import { useMarketingCookies } from '../useMarketingCookies'
import { useFlag } from 'containers/flags'

type Location = History.LocationDescriptor
type SessionsToRemove = 'all' | 'current' | 'none'

// Remove this function when the FF is removed
const isSignOutRefactoryFeatureFlagOn = (
  client: ApolloClient<object>
): boolean => {
  if (!client) return false

  try {
    const user = client.readQuery<GetInitialUserQuery>({
      query: GetInitialUserDocument,
    })

    if (!user) return false

    return user.me.featureFlags?.includes('signout-refactory') || false
  } catch (err) {
    return false
  }
}

interface SignOutProps {
  client: ApolloClient<object>
  history: History
  location?: Location
  removeDataFromSessions?: SessionsToRemove
  redirectBackAfterLoginAgain?: boolean
  reason?: string
}

declare module '@sketch/utils' {
  export interface EventsMap {
    signOut: {}
  }
}

// Function to be used in cases where a hook cannot be used.
export const signOut = async ({
  client,
  history,
  location,
  removeDataFromSessions = 'all',
  redirectBackAfterLoginAgain = true,
  reason = 'Uncategorized reason',
}: SignOutProps) => {
  const signInPageLocation: Location = location || {
    pathname: routes.SIGN_IN.create({}),
    state: redirectBackAfterLoginAgain
      ? {
          from: {
            pathname: window.location.pathname,
            search: window.location.search,
            state: {},
          },
        }
      : {},
  }

  Sentry.captureMessage(`Sign Out: ${reason}`, scope => {
    scope.addBreadcrumb({
      category: 'Authentication',
      message: 'Signing the user out',
      level: 'log',
    })

    return scope
  })

  if (isSignOutRefactoryFeatureFlagOn(client)) {
    if (removeDataFromSessions === 'none') {
      return
    }

    const allAuthorizations = getAllAuthorizations()

    /**
     * Since we received removeDataFromSessions as 'current' (what means that we wanna
     * remove data from the current session only) and we have more than one active
     * session, we don't need to remove all data, just remove the session from the
     * localStorage and Apollo Client Cache.
     */
    if (removeDataFromSessions === 'current' && allAuthorizations.length > 1) {
      const activeAuth = getActiveAuthorization(client.cache)

      // Remove the session from Apollo Client cache
      removeActiveAuthorization(client.cache)

      // Remove the session from local storage
      if (activeAuth?.fragment.authToken) {
        removeAuthorizationByAuthToken(activeAuth?.fragment.authToken)
      }

      return
    }

    /**
     * In this case, removeDataFromSessions is 'all' or is 'current' but we have only
     * one active session. In this case, we need to remove all data from local storage
     * and clean Apollo Client cache.
     */

    // This makes sure we don't have operations unfinished that might
    // cause error when the user logs out.
    client.stop()

    await client.clearStore()

    // We need to reinitialize the base apolloStore again because the state is now blank
    initializeLocalApolloState(
      (client as ApolloClient<NormalizedCacheObject>).cache
    )

    if (!redirectBackAfterLoginAgain) {
      removeItem(localStorageKeys.lastWorkspaceIdKey)
    }

    history.replace(signInPageLocation)

    Sentry.captureEvent({})

    return
  }

  /**
   * The order we follow when cleaning things up is important.
   * If we clean Apollo's cache before removing all local storage entries,
   * Apollo's cache subscriptions will be notified and, if they access the local
   * storage (e.g. to verify the list of authorizations), they will wrongly see
   * there are still ongoing sessions.
   */
  authorizationKeys.forEach(key => localStorage.removeItem(key))

  // This makes sure we don't have operations unfinished that might
  // cause error when the user logs out.
  client.stop()

  await client.clearStore()

  // We need to reinitialize the base apolloStore again because the state is now blank
  initializeLocalApolloState(
    (client as ApolloClient<NormalizedCacheObject>).cache
  )

  if (!redirectBackAfterLoginAgain) {
    removeItem(localStorageKeys.lastWorkspaceIdKey)
  }

  // This is basically to keep typescript nonsense validations quiet
  if (typeof signInPageLocation === 'string') {
    history.replace(signInPageLocation)
  } else {
    history.replace(signInPageLocation)
  }
}

interface UseSignOutProps {
  location?: Location
  revokeSessions?: SessionsToRemove
  removeDataFromSessions?: SessionsToRemove
  redirectBackAfterLoginAgain?: boolean
  reason?: string
}

/**
 * This hook should be used to revoke your sessions that you're actually logged in in the
 * current device and also remove all local variables related to the sessions. To revoke
 * sessions from other devices the user should use the sessions management section in the
 * /settings page.
 * revokeSessions and removeDataFromSessions params:
 * - 'all': will revoke/remove all sessions found in the localStorage
 * - 'current': will revoke/remove only the current session, stored in Apollo Client's cache
 * - 'none': no session will be revoked/removed within the BE
 */
const useSignOut = ({
  location,
  revokeSessions = 'all',
  removeDataFromSessions = 'all',
  redirectBackAfterLoginAgain = true,
  reason = 'Uncategorized reason',
}: UseSignOutProps = {}) => {
  const isSignOutRefactoryEnabled = useFlag('signout-refactory')
  const client = useApolloClient()
  const history = useHistory()
  const dispatchSignOutEvent = useEventDispatch('signOut')
  const { isLoaded, unloadChat } = useHubspotChat()
  const { removeCookies } = useMarketingCookies()

  const [revokeOAuth] = useRevokeOAuthMutation({
    onError: 'do-nothing',
  })

  if (isSignOutRefactoryEnabled) {
    return async () => {
      const allAuthorizations = getAllAuthorizations()

      // Revoking sessions
      if (revokeSessions === 'all') {
        allAuthorizations.forEach(authorization => {
          revokeOAuth({
            variables: {
              token: authorization.fragment.authToken,
            },
          })
        })
      } else if (revokeSessions === 'current') {
        const currentAuthorization = getUserAuthorization(client.cache)

        if (currentAuthorization) {
          revokeOAuth({
            variables: {
              token: currentAuthorization?.authToken,
            },
          })
        }
      }

      // Removing local variables
      await signOut({
        client,
        history,
        location,
        removeDataFromSessions,
        redirectBackAfterLoginAgain,
        reason,
      })

      if (
        removeDataFromSessions === 'all' ||
        (removeDataFromSessions === 'current' && allAuthorizations.length < 2)
      ) {
        /**
         * Since we want to remove all sessions or the session we're
         * trying to remove is the only one, we need to unload hubspot
         * chat, remove cookies and dispatch sign out event.
         */

        // Remove any hubspot chat if any
        if (isLoaded) {
          unloadChat()
        }

        // Remove marketing cookies
        removeCookies()

        dispatchSignOutEvent({})
      }
    }
  }

  // Old implementation to be removed when removing the Feature Flag
  return async () => {
    const allAuthorizations = getAllAuthorizations()

    allAuthorizations.forEach(authorization => {
      revokeOAuth({
        variables: {
          token: authorization.fragment.authToken,
        },
      })
    })

    await signOut({
      client,
      history,
      location,
      redirectBackAfterLoginAgain,
      reason,
    })

    // Remove any hubspot chat if any
    if (isLoaded) {
      unloadChat()
    }

    // Remove marketing cookies
    removeCookies()

    dispatchSignOutEvent({})
  }
}

export default useSignOut
